import moment from "moment";

/**
 * Provides logic for evaluating patient insurance discovery based on external API
 * results from pVerify.
 */
export default class InsuranceDiscovery {
  static context;

  /**
   * Sets the context.
   * @param {*} context - The context.
   */
  static setContext(context) {
    InsuranceDiscovery.context = context;
  }

  /**
   * Gets the insurance discovery data from the api result.
   * @param {*} apiResult - The api result.
   * @returns {Promise<Object>} The insurance discovery data.
   */
  static getInsuranceDiscovery(apiResult) {
    if (!apiResult || Object.keys(apiResult).length === 0) {
      return {
        status: "unknown",
        detail: {
          errorDescription: "No insurance discovery data received",
        },
      };
    }
    let insuranceDiscoveryData;
    try {
      insuranceDiscoveryData = JSON.parse(apiResult);
      // Check if response is an array or single object
      const responseCode = Array.isArray(insuranceDiscoveryData)
        ? insuranceDiscoveryData[0]?.APIResponseCode
        : insuranceDiscoveryData?.APIResponseCode;

      if (responseCode !== "0") {
        return {
          status: "error",
          insuranceDiscoveryData: null,
          detail: {
            errorDescription: "Invalid insurance discovery data format",
          },
        };
      }
    } catch (err) {
      return {
        status: "error",
        insuranceDiscoveryData: null,
        detail: {
          errorDescription: "Error parsing insurance discovery data",
        },
      };
    }

    // Normalize to array format for consistent handling
    if (!Array.isArray(insuranceDiscoveryData)) {
      insuranceDiscoveryData = [insuranceDiscoveryData];
    }

    return {
      status: "processed",
      insuranceDiscoveryData: insuranceDiscoveryData.map((data) => ({
        DOB: data?.DOB || data?.DemographicInfo?.Subscriber?.DOB_R,
        DOS: data?.DOS,
        PatientName:
          data?.FullName || data?.DemographicInfo?.Subscriber?.Fullname,
        Gender:
          data?.PlanCoverageSummary?.PatientGender ||
          data?.DemographicInfo?.Subscriber?.Gender_R,
        State: data?.State || data?.DemographicInfo?.Subscriber?.State,
        Address: data?.Address || data?.DemographicInfo?.Subscriber?.Address1,
        City: data?.City || data?.DemographicInfo?.Subscriber?.City,
        PostalCode:
          data?.PostalCode ||
          data?.Zip ||
          data?.DemographicInfo?.Subscriber?.Zip,
        ComboPayerResponses: data?.ComboPayerResponses,
      })),
    };
  }

  /**
   * Gets the missing patient fields for insurance discovery.
   * @param {*} patient - The patient.
   * @returns {Array} The missing patient fields.
   */
  static getInsurnaceDiscoveryMissingPatientFields(patient) {
    const missingFields = [];
    if (!patient?.firstName) {
      missingFields.push("First Name");
    }
    if (!patient?.lastName) {
      missingFields.push("Last Name");
    }
    if (!patient?.gender) {
      missingFields.push("Gender");
    }
    if (!patient?.state) {
      missingFields.push("State");
    }
    return missingFields;
  }
}
